import { useState, useEffect } from 'react';
import { farmersAPI, processorsAPI } from '../services/api';

/**
 * Shared hook that fetches farmer/processor data and computes
 * aggregate statistics used by Homepage, WelcomePage, and About.
 *
 * @returns {{ farmerCount, processorCount, municipalityCount, totalHectares, loading, error }}
 */
export function useSystemStats() {
  const [stats, setStats] = useState({
    farmerCount: 0,
    processorCount: 0,
    municipalityCount: 0,
    totalHectares: 0,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const [farmersResponse, processorsResponse] = await Promise.all([
          farmersAPI.getAll(),
          processorsAPI.getAll(),
        ]);

        const farmersData =
          farmersResponse?.data ?? farmersResponse?.rows ?? farmersResponse ?? [];
        const processorsData =
          processorsResponse?.data ?? processorsResponse?.rows ?? processorsResponse ?? [];

        const farmerCount = Array.isArray(farmersData) ? farmersData.length : 0;
        const processorCount = Array.isArray(processorsData) ? processorsData.length : 0;

        const municipalitiesSet = new Set();

        if (Array.isArray(farmersData)) {
          farmersData.forEach((farmer) => {
            const m = farmer.Municipality || farmer.municipality;
            if (m) municipalitiesSet.add(m.trim());
          });
        }

        if (Array.isArray(processorsData)) {
          processorsData.forEach((processor) => {
            const m = processor.Municipality || processor.municipality;
            if (m) municipalitiesSet.add(m.trim());
          });
        }

        let totalHectares = 0;
        if (Array.isArray(farmersData)) {
          farmersData.forEach((farmer) => {
            const farmSize =
              farmer.Farm_Area_ha || farmer.farmArea || farmer.farm_area || 0;
            const hectares = parseFloat(farmSize);
            if (!isNaN(hectares)) totalHectares += hectares;
          });
        }

        if (!cancelled) {
          setStats({
            farmerCount,
            processorCount,
            municipalityCount: municipalitiesSet.size,
            totalHectares: Math.round(totalHectares),
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setStats({
            farmerCount: 0,
            processorCount: 0,
            municipalityCount: 0,
            totalHectares: 0,
            loading: false,
            error: 'Unable to load statistics. Please try again later.',
          });
        }
      }
    };

    fetchStats();
    return () => { cancelled = true; };
  }, []);

  return stats;
}
